import {
  NEGOTIABLE_PLAN_TYPES,
  PAYMENT_BIWEEKLY_CADENCES,
  PAYMENT_FREQUENCIES,
  PAYMENT_FREQUENCY_MAPPINGS,
  PAYMENT_METHODS,
  PAYMENT_PLAN_TYPES,
} from "../constants";

export const paymentMethodNavigationOptions = (methodType) =>
  Object.values(PAYMENT_METHODS).includes(methodType)
    ? { state: { methodType } }
    : undefined;

const numberOrNull = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const moneyToCents = (value) => Math.round(Number(value) * 100);

export const proposedFailedPaymentRecoveryPlan = (
  totalPaymentAmount,
  failedPaymentAmount
) => {
  const totalCents = moneyToCents(totalPaymentAmount);
  const failedPaymentCents = moneyToCents(failedPaymentAmount);

  if (
    !Number.isFinite(totalCents) ||
    !Number.isFinite(failedPaymentCents) ||
    totalCents <= 0 ||
    failedPaymentCents <= 0
  ) {
    return null;
  }
  if (totalCents <= failedPaymentCents) {
    return {
      totalPaymentAmount: totalCents / 100,
      numPayments: 1,
      standardPaymentAmount: totalCents / 100,
      lastPaymentAmount: null,
    };
  }

  const nearestCount = Math.max(
    1,
    Math.round(totalCents / failedPaymentCents)
  );
  const nearestFinalCents =
    totalCents - failedPaymentCents * (nearestCount - 1);
  const maximumEqualSplitRoundingCents = nearestCount - 1;
  const isRoundedEqualSplit =
    nearestFinalCents > 0 &&
    Math.abs(nearestFinalCents - failedPaymentCents) <=
      maximumEqualSplitRoundingCents;
  const numPayments = isRoundedEqualSplit
    ? nearestCount
    : Math.ceil(totalCents / failedPaymentCents);
  const finalPaymentCents =
    totalCents - failedPaymentCents * (numPayments - 1);
  const lastPaymentAmount =
    finalPaymentCents === failedPaymentCents
      ? null
      : finalPaymentCents / 100;

  return {
    totalPaymentAmount: totalCents / 100,
    numPayments,
    standardPaymentAmount: failedPaymentCents / 100,
    lastPaymentAmount,
  };
};

export const proposedPlanFromRepaymentOptions = (
  repaymentOptions,
  totalPaymentAmount
) => {
  const monthlyOptions =
    repaymentOptions?.planOptions?.[PAYMENT_FREQUENCIES.monthly];

  if (!Array.isArray(monthlyOptions) || monthlyOptions.length === 0) {
    return null;
  }

  const validMonthlyOptions = monthlyOptions
    .map((option) => {
      const planTerm = numberOrNull(option?.planTerm);
      const planScheduleFrequency = numberOrNull(option?.planScheduleFrequency);
      const standard = numberOrNull(option?.perPaymentAmount?.standard);
      const last = numberOrNull(option?.perPaymentAmount?.last ?? standard);

      if (
        planTerm === null ||
        planScheduleFrequency === null ||
        standard === null ||
        last === null
      ) {
        return null;
      }

      return {
        numPayments: planTerm * planScheduleFrequency,
        planScheduleFrequency,
        perPaymentAmount: { standard, last },
      };
    })
    .filter(Boolean);

  if (validMonthlyOptions.length === 0) {
    return null;
  }

  const selectedMonthlyOption = validMonthlyOptions.sort((a, b) => {
    const standardDiff =
      a.perPaymentAmount.standard - b.perPaymentAmount.standard;
    if (standardDiff !== 0) return standardDiff;
    return b.numPayments - a.numPayments;
  })[0];

  return {
    currentBalance: totalPaymentAmount,
    proposedAmountApproved: true,
    planOptions: {
      [PAYMENT_FREQUENCIES.monthly]: selectedMonthlyOption,
    },
  };
};

// transform a negotiate-plan response into a paymentPlan.
// If no planType is given, it will be omitted from the result.
export const paymentPlanFromPlanOption = (response, frequency, planType) => {
  const {
    numPayments,
    planScheduleFrequency,
    perPaymentAmount: {
      standard: standardPaymentAmount,
      last: lastPaymentAmount,
    },
  } = response?.planOptions?.[frequency] || {};

  if (
    planType === NEGOTIABLE_PLAN_TYPES.paymentPlan ||
    planType === NEGOTIABLE_PLAN_TYPES.specialPaymentPlan
  ) {
    planType =
      numPayments === 1
        ? PAYMENT_PLAN_TYPES.payInFull
        : PAYMENT_PLAN_TYPES.paymentPlan;
  } else if (planType === NEGOTIABLE_PLAN_TYPES.settlementPlan) {
    (planType === numPayments) === 1
      ? PAYMENT_PLAN_TYPES.settleInFull
      : PAYMENT_PLAN_TYPES.settlementPlan;
  }

  return {
    planType,
    numPayments,
    paymentFrequency: PAYMENT_FREQUENCY_MAPPINGS[planScheduleFrequency],
    standardPaymentAmount,
    lastPaymentAmount,
    totalPaymentAmount: response?.currentBalance,
  };
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;
// A schedule keeps its cadence when an installment was moved (which breaks the
// gaps on both sides of it) or the final payment lands off-cycle, so only half
// of the gaps must fit.
const REGULAR_GAP_SHARE = 0.5;
const SEMI_MONTHLY_DAY_SHARE = 0.75;

const scheduleCalendarDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const time = Date.UTC(year, month, day);
  return {
    day,
    weekday: new Date(time).getUTCDay(),
    isLastDayOfMonth: day === new Date(Date.UTC(year, month + 1, 0)).getUTCDate(),
    time,
  };
};

const shareMatching = (items, predicate) =>
  items.length ? items.filter(predicate).length / items.length : 0;

const mostlyMatch = (items, predicate) =>
  shareMatching(items, predicate) >= REGULAR_GAP_SHARE;

const gapBetween = (min, max) => (gap) => gap >= min && gap <= max;

const mostCommon = (values) => {
  const counts = new Map();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  let best = null;
  for (const [value, count] of counts) {
    if (best === null || count > counts.get(best)) best = value;
  }
  return best;
};

const SEMI_MONTHLY_CADENCES = [
  {
    cadence: PAYMENT_BIWEEKLY_CADENCES.firstAndFifteenth,
    matches: ({ day }) => day === 1 || day === 15,
  },
  {
    cadence: PAYMENT_BIWEEKLY_CADENCES.fifteenthAndLastDay,
    matches: ({ day, isLastDayOfMonth }) => day === 15 || isLastDayOfMonth,
  },
];

const INTERVAL_FREQUENCIES = [
  { frequency: PAYMENT_FREQUENCIES.weekly, matches: gapBetween(6, 8) },
  { frequency: PAYMENT_FREQUENCIES.everyTwoWeeks, matches: gapBetween(13, 15) },
  { frequency: PAYMENT_FREQUENCIES.monthly, matches: gapBetween(28, 31) },
];

// Interprose stores a plan only as dated installments, so the cadence it was
// created with is recovered from those dates. Interprose "biweekly" plans are
// semi-monthly (1st/15th or 15th/last day), distinct from every two weeks.
// frequency is null only when the dates follow no recognizable cadence.
// dayOfMonth and dayOfWeek are the recurring payment day, taken from the whole
// schedule so a moved first installment does not change it.
export const paymentCadenceFromSchedule = (schedule) => {
  const dates = (Array.isArray(schedule) ? schedule : [])
    .map((payment) => scheduleCalendarDate(payment?.paymentDate))
    .filter(Boolean)
    .sort((left, right) => left.time - right.time);
  const cadence = (frequency, biweeklyCadence = null) => ({
    frequency,
    biweeklyCadence,
    dayOfMonth:
      frequency === PAYMENT_FREQUENCIES.monthly
        ? mostCommon(dates.map(({ day }) => day))
        : null,
    dayOfWeek: [PAYMENT_FREQUENCIES.weekly, PAYMENT_FREQUENCIES.everyTwoWeeks].includes(frequency)
      ? mostCommon(dates.map(({ weekday }) => weekday))
      : null,
  });

  if (dates.length < 2) return cadence(PAYMENT_FREQUENCIES.monthly);

  const gaps = dates
    .slice(1)
    .map((date, index) => (date.time - dates[index].time) / DAY_IN_MS);

  if (mostlyMatch(gaps, gapBetween(12, 17))) {
    // Every-14-days dates can land on the 1st and 15th for a while (Feb 1,
    // Feb 15, Mar 1, Mar 15, Mar 29), so semi-monthly must fit the days at
    // least as well as every two weeks fits the gaps.
    const everyTwoWeeksShare = shareMatching(gaps, gapBetween(13, 15));
    const semiMonthly = SEMI_MONTHLY_CADENCES.find(({ matches }) => {
      const dayShare = shareMatching(dates, matches);
      return dayShare >= SEMI_MONTHLY_DAY_SHARE && dayShare >= everyTwoWeeksShare;
    });
    if (semiMonthly) {
      return cadence(PAYMENT_FREQUENCIES.biweekly, semiMonthly.cadence);
    }
  }

  const interval = INTERVAL_FREQUENCIES.find(({ matches }) =>
    mostlyMatch(gaps, matches)
  );
  return cadence(interval?.frequency ?? null);
};

export const paymentFrequencyFromSchedule = (schedule) =>
  paymentCadenceFromSchedule(schedule).frequency;

// Discount types
const DISCOUNT_TYPES = {
  SETTLEMENT: "SETTLEMENT",
  PROMO_CODE: "PROMO_CODE",
};

export const paymentPlanPriceFromPlan = (
  paymentPlan,
  settlementOffer,
  promoCode
) => {
  const { discountAmount } = promoCode || {};

  const { planType, numPayments, standardPaymentAmount, lastPaymentAmount } =
    paymentPlan;

  // Initialize payments array
  const payments = [];

  // Handle settlement offer case
  if (settlementOffer) {
    const { offerAmount, currentBalance } = settlementOffer;

    // Calculate settlement discount percentage
    const settlementDiscountPercentage =
      (currentBalance - offerAmount) / currentBalance;

    // Calculate old payment amounts (before settlement discount)
    const oldStandardPaymentAmount =
      standardPaymentAmount / (1 - settlementDiscountPercentage);
    const oldLastPaymentAmount = lastPaymentAmount
      ? lastPaymentAmount / (1 - settlementDiscountPercentage)
      : oldStandardPaymentAmount;

    // Calculate settlement discount amounts
    const settlementStandardDiscount =
      oldStandardPaymentAmount - standardPaymentAmount;
    const settlementLastDiscount =
      oldLastPaymentAmount - (lastPaymentAmount || standardPaymentAmount);

    // Create payment objects for each payment with settlement discount
    for (let i = 0; i < numPayments; i++) {
      const isLastPayment = i === numPayments - 1;
      const oldAmount =
        isLastPayment && lastPaymentAmount
          ? oldLastPaymentAmount
          : oldStandardPaymentAmount;
      const baseAmount =
        isLastPayment && lastPaymentAmount
          ? lastPaymentAmount
          : standardPaymentAmount;
      const settlementDiscount =
        isLastPayment && lastPaymentAmount
          ? settlementLastDiscount
          : settlementStandardDiscount;

      const payment = {
        oldPaymentAmount: oldAmount,
        paymentAmount: baseAmount,
        discountsApplied: [
          {
            amount: settlementDiscount,
            type: DISCOUNT_TYPES.SETTLEMENT,
          },
        ],
      };

      payments.push(payment);
    }
  } else {
    // Handle no settlement offer case - use original payment plan amounts
    for (let i = 0; i < numPayments; i++) {
      const isLastPayment = i === numPayments - 1;
      const baseAmount =
        isLastPayment && lastPaymentAmount
          ? lastPaymentAmount
          : standardPaymentAmount;

      const payment = {
        oldPaymentAmount: baseAmount, // No settlement discount, so old amount = current amount
        paymentAmount: baseAmount,
        discountsApplied: [],
      };

      payments.push(payment);
    }
  }

  // Apply promo code discounts from last payment to first
  if (promoCode && discountAmount) {
    let remainingPromoDiscount = discountAmount;

    // Apply promo discount from last payment to first
    for (let i = numPayments - 1; i >= 0 && remainingPromoDiscount > 0; i--) {
      const payment = payments[i];
      const applicableDiscount = Math.min(
        remainingPromoDiscount,
        payment.paymentAmount
      );

      if (applicableDiscount > 0) {
        // Update payment amount
        payment.paymentAmount -= applicableDiscount;

        // Add promo code discount to discountsApplied
        payment.discountsApplied.push({
          amount: applicableDiscount,
          type: DISCOUNT_TYPES.PROMO_CODE,
        });

        remainingPromoDiscount -= applicableDiscount;
      }
    }
  }

  return {
    ...paymentPlan,
    payments,
  };
};

// Group consecutive payments that are identical (same amount and discounts)
export const groupConsecutivePayments = (payments) => {
  if (!payments || payments.length === 0) {
    return [];
  }

  const groupedPayments = [];
  let currentGroup = {
    ...payments[0],
    count: 1,
  };

  for (let i = 1; i < payments.length; i++) {
    const currentPayment = payments[i];
    const prevPayment = payments[i - 1];

    // Check if payments are identical (same amount and same discounts)
    const sameAmount =
      currentPayment.paymentAmount === prevPayment.paymentAmount;
    const sameOldAmount =
      currentPayment.oldPaymentAmount === prevPayment.oldPaymentAmount;
    const sameDiscounts =
      JSON.stringify(currentPayment.discountsApplied) ===
      JSON.stringify(prevPayment.discountsApplied);

    if (sameAmount && sameOldAmount && sameDiscounts) {
      // Same as previous payment, increment count
      currentGroup.count++;
    } else {
      // Different payment, add current group to result and start new group
      groupedPayments.push(currentGroup);
      currentGroup = {
        ...currentPayment,
        count: 1,
      };
    }
  }

  // Add the last group
  groupedPayments.push(currentGroup);

  return groupedPayments;
};

export const FAILED_PAYMENT_POPUP_SOURCE = "failed-payment-popup";

export const SETTLED_PLAN_LAPSE_STATUSES = {
  active: "ACTIVE",
  lapsed: "LAPSED",
};

// A lapsed settlement plan's terms are no longer offered, so its missed-payment
// recovery must be priced from the current offer. An active plan from
// /payment-plans means it was replaced, not lapsed.
export const isSettledPlanLapsed = (summary, { hasNoActivePlans } = {}) =>
  summary?.plan == null &&
  summary?.settledPlan?.lapseStatus === SETTLED_PLAN_LAPSE_STATUSES.lapsed &&
  hasNoActivePlans === true;

// Rebuilds missed-payment recovery terms for a lapsed settlement plan from the
// account's current settlement offer, so the restart can never reuse the lapsed
// plan's previous (possibly lower) settlement. Picks the fewest offered terms
// whose installment does not exceed the lapsed installment.
export const lapsedSettlementRecoveryPlan = (offer, previousInstallmentAmount) => {
  const offerCents = moneyToCents(offer?.offerAmount);
  const rawTerms = Array.isArray(offer?.termsOffered)
    ? offer.termsOffered
    : [offer?.termsOffered];
  const terms = [
    ...new Set(
      rawTerms.map(Number).filter((term) => Number.isInteger(term) && term > 0)
    ),
  ].sort((a, b) => a - b);
  if (!Number.isFinite(offerCents) || offerCents <= 0 || terms.length === 0) {
    return null;
  }

  const targetCount =
    proposedFailedPaymentRecoveryPlan(offerCents / 100, previousInstallmentAmount)
      ?.numPayments ?? terms[terms.length - 1];
  const numPayments =
    terms.find((term) => term >= targetCount) ?? terms[terms.length - 1];
  const standardCents = Math.floor(offerCents / numPayments);
  const lastCents = offerCents - standardCents * (numPayments - 1);

  return {
    totalPaymentAmount: offerCents / 100,
    numPayments,
    standardPaymentAmount: standardCents / 100,
    lastPaymentAmount:
      numPayments > 1 && lastCents !== standardCents ? lastCents / 100 : null,
    planType:
      numPayments === 1
        ? PAYMENT_PLAN_TYPES.settleInFull
        : PAYMENT_PLAN_TYPES.settlementPlan,
    paymentFrequency: PAYMENT_FREQUENCIES.monthly,
  };
};

// Without a current settlement offer, a lapsed settlement plan recovers on the
// account's current monthly repayment option rather than its lapsed terms.
export const lapsedRepaymentRecoveryPlan = (proposedPlan) => {
  if (!proposedPlan?.planOptions?.[PAYMENT_FREQUENCIES.monthly]) return null;
  const plan = paymentPlanFromPlanOption(
    proposedPlan,
    PAYMENT_FREQUENCIES.monthly,
    NEGOTIABLE_PLAN_TYPES.paymentPlan
  );
  const numPayments = numberOrNull(plan.numPayments);
  const standardPaymentAmount = numberOrNull(plan.standardPaymentAmount);
  const totalPaymentAmount = numberOrNull(plan.totalPaymentAmount);
  if (!(numPayments > 0) || !(standardPaymentAmount > 0) || !(totalPaymentAmount > 0)) {
    return null;
  }
  const lastPaymentAmount = numberOrNull(plan.lastPaymentAmount);

  return {
    totalPaymentAmount,
    numPayments,
    standardPaymentAmount,
    lastPaymentAmount:
      lastPaymentAmount !== null && lastPaymentAmount !== standardPaymentAmount
        ? lastPaymentAmount
        : null,
    planType: plan.planType,
    paymentFrequency: plan.paymentFrequency || PAYMENT_FREQUENCIES.monthly,
  };
};

export const isPaymentStartWithinLimit = (
  paymentStartDate,
  limitDays,
  today = new Date()
) => {
  if (!paymentStartDate) return true;
  const startDate = new Date(paymentStartDate);
  if (Number.isNaN(startDate.getTime())) return false;
  startDate.setHours(0, 0, 0, 0);
  const currentDate = new Date(today);
  currentDate.setHours(0, 0, 0, 0);
  return Math.round((startDate - currentDate) / DAY_IN_MS) <= Number(limitDays);
};

export const resolveFailedPaymentRecoveryRouteState = (routeState) => {
  const paymentPlan = routeState?.failedPaymentRecoveryPlan;
  const selectedStep = routeState?.failedPaymentRecoveryStep;

  if (
    routeState?.source !== FAILED_PAYMENT_POPUP_SOURCE ||
    selectedStep !== "setup" ||
    paymentPlan?.source !== FAILED_PAYMENT_POPUP_SOURCE ||
    !paymentPlan?.planType
  ) {
    return null;
  }

  return { paymentPlan, selectedStep };
};

export const isFailedPaymentPlanReplacement = (paymentPlan, location) =>
  Boolean(
    paymentPlan?.failedPaymentDismissalId &&
      (paymentPlan?.source === FAILED_PAYMENT_POPUP_SOURCE ||
        location?.state?.source === FAILED_PAYMENT_POPUP_SOURCE)
  );

export const isActiveFailedPaymentPlanReplacement = (paymentPlan, location) =>
  Boolean(
    isFailedPaymentPlanReplacement(paymentPlan, location) &&
      paymentPlan?.replaceExistingActivePlan === true &&
      paymentPlan?.failedPaymentUpdateIdempotencyKey
  );

